import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  Checkbox,
  Loader,
  Center,
  Alert,
  Tabs,
  Tooltip,
  Pill,
  Anchor,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconAlertCircle,
  IconPackage,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  formatSendOutExternalReason,
  stripSendOutHtml,
} from "../../utils/sendOut";
import {
  useCasesPendingShipping,
  useConfirmShipping,
} from "../../hooks/useWorkflows";
import {
  usePendingShippingSendOuts,
  useMarkShipped,
} from "../../hooks/useSendOuts";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useTaskStore } from "../../store/task.store";
import {
  ShippingQueueCase,
  ConfirmShippingResult,
} from "../../services/workflows.service";
import {
  SendOut,
  SendoutRequestType,
  ExternalInstitutionReason,
} from "../../services/sendOuts.service";
import ShippedCasesPanel from "../../components/shipping/ShippedCasesPanel";

dayjs.extend(relativeTime);

const ITEMS_PER_PAGE = 20;

// ==================== CONSTANTS ====================

const typeFilterOptions = [
  { value: "remote", label: "Remote" },
  { value: "tc_return", label: "TC Return" },
];

const REQUEST_TYPE_OPTIONS = [
  {
    value: SendoutRequestType.PHYSICIAN_REQUEST,
    label: "Ordering Physician Requesting Consultation",
  },
  { value: SendoutRequestType.EXTERNAL_INSTITUTION, label: "External Request" },
  { value: SendoutRequestType.MATERIAL_RETURN, label: "Material Return" },
  {
    value: SendoutRequestType.GCP_CONSULT,
    label: "Pathologist Requires Expert Consultation",
  },
];

const REQUEST_TYPE_SHORT_LABELS: Record<string, string> = {
  [SendoutRequestType.PHYSICIAN_REQUEST]: "Physician Request",
  [SendoutRequestType.EXTERNAL_INSTITUTION]: "External Request",
  [SendoutRequestType.GCP_CONSULT]: "GCP Consult",
  [SendoutRequestType.MATERIAL_RETURN]: "Material Return",
};

const PRIORITY_OPTIONS = [
  { value: "stat", label: "STAT" },
  { value: "urgent", label: "Urgent" },
  { value: "routine", label: "Routine" },
];

const PRIORITY_LABELS: Record<string, string> = {
  stat: "STAT",
  urgent: "Urgent",
  routine: "Routine",
};

const EXTERNAL_REASON_OPTIONS = [
  {
    value: ExternalInstitutionReason.CONTINUATION_OF_CARE,
    label: "Continuation of Care",
  },
  { value: ExternalInstitutionReason.LEGAL_REQUEST, label: "Legal Request" },
  {
    value: ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING,
    label: "Outside Request for Additional Testing",
  },
  { value: ExternalInstitutionReason.OTHER, label: "Other" },
];

const EXTERNAL_REASON_SHORT_LABELS: Record<string, string> = {
  [ExternalInstitutionReason.CONTINUATION_OF_CARE]: "Continuation of Care",
  [ExternalInstitutionReason.LEGAL_REQUEST]: "Legal Request",
  [ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING]:
    "Outside Req. / Add. Testing",
  [ExternalInstitutionReason.OTHER]: "Other",
};

// ==================== TYPES ====================

interface CaseFilters {
  search: string;
  page: number;
  caseType: string | null;
  pathologists: string[];
  sendingPractices: string[];
}

interface SendOutFilters {
  search: string;
  page: number;
  requestTypes: string[];
  priorities: string[];
  destinations: string[];
  requestedBys: string[];
  externalInstitutionReasons: string[];
}

const defaultCaseFilters = (): CaseFilters => ({
  search: "",
  page: 1,
  caseType: null,
  pathologists: [],
  sendingPractices: [],
});

const defaultSendOutFilters = (): SendOutFilters => ({
  search: "",
  page: 1,
  requestTypes: [],
  priorities: [],
  destinations: [],
  requestedBys: [],
  externalInstitutionReasons: [],
});

// ==================== HELPERS ====================

export function getCaseType(
  accessioningType: string | null
): "remote" | "tc_return" {
  if (accessioningType === "tc_only") {
    return "tc_return";
  }
  return "remote";
}

export function getTypeLabel(accessioningType: string | null): string {
  const type = getCaseType(accessioningType);
  return type === "tc_return" ? "TC Return" : "Remote";
}

export function getShippingAddress(caseData: ShippingQueueCase): {
  display: string;
  hasAddress: boolean;
} {
  const type = getCaseType(caseData.accessioningType);

  if (type === "tc_return") {
    if (caseData.shippingStreet) {
      const parts = [
        caseData.shippingStreet,
        caseData.shippingCity,
        caseData.shippingState,
        caseData.shippingZipCode,
      ].filter(Boolean);
      const addressLine = parts.join(", ");
      const recipient = caseData.shippingPathologist || "TC Return";
      return { display: `${recipient}, ${addressLine}`, hasAddress: true };
    }
    const surgeryCenter = caseData.originatingSurgeryCenter;
    if (surgeryCenter?.address) {
      return {
        display: `${surgeryCenter.name}, ${surgeryCenter.address}`,
        hasAddress: true,
      };
    }
    return { display: "", hasAddress: false };
  }
  const pathologist = caseData.pathologist;
  if (pathologist?.remoteAddress) {
    return {
      display: `${pathologist.fullName}, ${pathologist.remoteAddress}`,
      hasAddress: true,
    };
  }
  return { display: "", hasAddress: false };
}

export function getSendOutTypeLabel(requestType: SendoutRequestType): string {
  switch (requestType) {
    case SendoutRequestType.PHYSICIAN_REQUEST:
      return "Ordering Physician Requesting Consultation";
    case SendoutRequestType.EXTERNAL_INSTITUTION:
      return "External Request";
    case SendoutRequestType.GCP_CONSULT:
      return "Pathologist Requires Expert Consultation";
    case SendoutRequestType.MATERIAL_RETURN:
      return "Material Return";
    default:
      return "Send-Out";
  }
}

export function getSendOutDestination(sendOut: SendOut): string {
  if (sendOut.externalInstitution?.name)
    return sendOut.externalInstitution.name;
  if (sendOut.stainProvider?.name) return sendOut.stainProvider.name;
  return "N/A";
}

export function getSendOutPriorityBadge(sendOut: SendOut) {
  if (!sendOut.priority) return null;
  const map: Record<string, { color: string; label: string }> = {
    stat: { color: "red", label: "STAT" },
    urgent: { color: "orange", label: "Urgent" },
    routine: { color: "blue", label: "Routine" },
  };
  const p = map[sendOut.priority] ?? { color: "gray", label: sendOut.priority };
  return (
    <Badge color={p.color} variant="filled" size="xs">
      {p.label}
    </Badge>
  );
}

export function getSendOutMaterialsSummary(sendOut: SendOut): string {
  const slideCount = sendOut.slidesSent?.length || 0;
  const blockCount = sendOut.blocksSent?.length || 0;
  if (slideCount > 0 && blockCount > 0)
    return `${slideCount} slide${
      slideCount > 1 ? "s" : ""
    } across ${blockCount} block${blockCount > 1 ? "s" : ""}`;
  if (slideCount > 0) return `${slideCount} slide${slideCount > 1 ? "s" : ""}`;
  if (blockCount > 0) return `${blockCount} block${blockCount > 1 ? "s" : ""}`;
  return "No materials";
}

export function formatSendOutUserName(
  user?: { fullName: string } | null
): string {
  return user?.fullName ?? "N/A";
}

export function isCaseBlocked(caseData: ShippingQueueCase): boolean {
  if (caseData.shippingEligibility !== undefined) {
    return (
      caseData.shippingEligibility === "blocked" ||
      caseData.shippingEligibility === "not_required"
    );
  }
  return !getShippingAddress(caseData).hasAddress;
}

export interface StatusBadgeConfig {
  color: string;
  label: string;
  show: boolean;
}

export function getStatusBadgeConfig(
  caseData: ShippingQueueCase
): StatusBadgeConfig {
  const eligibility = caseData.shippingEligibility;
  if (eligibility === "ready") {
    return { color: "green", label: "Ready to ship", show: true };
  }
  if (eligibility === "blocked") {
    return { color: "red", label: "Blocked", show: true };
  }
  if (eligibility === "not_required") {
    return { color: "gray", label: "Not Required", show: false };
  }

  // Fallback for undefined
  const hasAddress = getShippingAddress(caseData).hasAddress;
  if (hasAddress) {
    return { color: "green", label: "Ready to ship", show: true };
  }
  return { color: "red", label: "Blocked", show: true };
}

function handleShippingNotification(result: ConfirmShippingResult) {
  if (result.confirmedCount > 0) {
    notifications.show({
      title: "Success",
      message: `Confirmed shipping for ${result.confirmedCount} case(s)`,
      color: "green",
    });
  }

  const skippedCount = result.skippedCases?.length || 0;
  const blockedCount = result.blockedCases?.length || 0;

  if (skippedCount > 0 || blockedCount > 0) {
    const errorDetails: Array<{ caseNumber: string; message: string }> = [];

    if (result.blockedCases && result.blockedCases.length > 0) {
      result.blockedCases.forEach((bc) => {
        errorDetails.push({
          caseNumber: bc.caseNumber,
          message:
            bc.shippingBlockMessage || "Shipping address missing or invalid.",
        });
      });
    }

    if (result.skippedCases && result.skippedCases.length > 0) {
      result.skippedCases.forEach((sc) => {
        const alreadyAdded = errorDetails.some((e) => e.caseNumber === sc);
        if (!alreadyAdded) {
          errorDetails.push({
            caseNumber: sc,
            message: "Missing or invalid address.",
          });
        }
      });
    }

    notifications.show({
      title: "Shipping Skipped / Blocked",
      color: "red",
      autoClose: 10000,
      message: (
        <Stack gap="xs" mt="xs">
          <Text size="sm">The following case(s) were skipped or blocked:</Text>
          <Stack gap={4} pl="xs">
            {errorDetails.map((err, index) => (
              <Text key={index} size="xs" fw={500} c="red.7">
                • <strong>{err.caseNumber}</strong>: {err.message}
              </Text>
            ))}
          </Stack>
          <Text size="xs" c="dimmed" mt="xs" style={{ whiteSpace: "normal" }}>
            Add the remote shipping address in Admin → Users/Physicians, then
            retry.
          </Text>
        </Stack>
      ),
    });
  }
}

// ==================== COMPONENT ====================

export default function ShippingQueuePage() {
  const { permittedTasks, selectedTasks, setSelectedTasks } = useTaskStore();
  const navigate = useNavigate();

  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const [activeTab, setActiveTab] = useState<"cases" | "send-outs">("cases");
  const [activeCasesTab, setActiveCasesTab] = useState<"pending" | "shipped">(
    "pending"
  );

  const [caseFilters, setCaseFilters] =
    useState<CaseFilters>(defaultCaseFilters);
  const [pendingCaseFilters, setPendingCaseFilters] =
    useState<CaseFilters>(defaultCaseFilters);
  const [sendOutFilters, setSendOutFilters] = useState<SendOutFilters>(
    defaultSendOutFilters
  );
  const [pendingSendOutFilters, setPendingSendOutFilters] =
    useState<SendOutFilters>(defaultSendOutFilters);

  const [debouncedCaseSearch] = useDebouncedValue(caseFilters.search, 300);
  const [debouncedSendOutSearch] = useDebouncedValue(
    sendOutFilters.search,
    300
  );

  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  const bulkTrackingForm = useForm({
    initialValues: { trackingNumber: "" },
    validateInputOnBlur: true,
  });
  const [singleConfirmCase, setSingleConfirmCase] =
    useState<ShippingQueueCase | null>(null);
  const [sendOutToConfirm, setSendOutToConfirm] = useState<SendOut | null>(
    null
  );

  const {
    data: pendingCases = [],
    isLoading,
    isFetching,
    refetch,
  } = useCasesPendingShipping();
  const confirmMutation = useConfirmShipping();

  const {
    data: pendingSendOuts = [],
    isLoading: loadingSendOuts,
    isFetching: fetchingSendOuts,
    refetch: refetchSendOuts,
  } = usePendingShippingSendOuts();
  const markShippedMutation = useMarkShipped();

  const canManageUsers = useHasPermission("users.manage");
  const canManagePhysicians = useHasPermission("physicians.manage");
  const canUpdateCases = useHasPermission("cases.update");

  const shippingQueueCases = useMemo(
    () => pendingCases.filter((c) => c.accessioningType !== "tc_only"),
    [pendingCases]
  );

  const handleAdminRemediationClick = useCallback(
    (targetPath: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      const adminTask = permittedTasks.find((t) => t.slug === "admin");
      if (adminTask && !selectedTasks.some((t) => t.slug === "admin")) {
        setSelectedTasks([...selectedTasks, adminTask]);
      }
      navigate(targetPath);
    },
    [permittedTasks, selectedTasks, setSelectedTasks, navigate]
  );

  // ==================== DERIVED OPTIONS ====================

  const pathologistOptions = useMemo(() => {
    const names = new Set(
      shippingQueueCases
        .map((c) => c.pathologist?.fullName)
        .filter((n): n is string => !!n)
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [shippingQueueCases]);

  const sendingPracticeOptions = useMemo(() => {
    const names = new Set(
      shippingQueueCases
        .map((c) => c.sendingPractice?.name)
        .filter((n): n is string => !!n)
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [shippingQueueCases]);

  const sendOutDestinationOptions = useMemo(() => {
    const names = new Set(
      pendingSendOuts.map(getSendOutDestination).filter((n) => n !== "N/A")
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [pendingSendOuts]);

  const sendOutRequestedByOptions = useMemo(() => {
    const names = new Set(
      pendingSendOuts
        .map((s) => s.requestedBy?.fullName)
        .filter((n): n is string => !!n)
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [pendingSendOuts]);

  // ==================== FILTERED DATA ====================

  const filteredCases = useMemo(() => {
    let result = [...shippingQueueCases];

    if (debouncedCaseSearch) {
      const q = debouncedCaseSearch.toLowerCase();
      result = result.filter((c) => {
        const caseMatch = c.caseNumber.toLowerCase().includes(q);
        const patientName = c.patient
          ? `${c.patient.firstName} ${c.patient.lastName}`.toLowerCase()
          : "";
        return caseMatch || patientName.includes(q);
      });
    }

    if (caseFilters.caseType) {
      result = result.filter(
        (c) => getCaseType(c.accessioningType) === caseFilters.caseType
      );
    }

    if (caseFilters.pathologists.length > 0) {
      result = result.filter(
        (c) =>
          c.pathologist?.fullName != null &&
          caseFilters.pathologists.includes(c.pathologist.fullName)
      );
    }

    if (caseFilters.sendingPractices.length > 0) {
      result = result.filter(
        (c) =>
          c.sendingPractice?.name != null &&
          caseFilters.sendingPractices.includes(c.sendingPractice.name)
      );
    }

    return result;
  }, [
    shippingQueueCases,
    debouncedCaseSearch,
    caseFilters.caseType,
    caseFilters.pathologists,
    caseFilters.sendingPractices,
  ]);

  const filteredSendOuts = useMemo(() => {
    let result = [...pendingSendOuts];

    if (debouncedSendOutSearch) {
      const q = debouncedSendOutSearch.toLowerCase();
      result = result.filter(
        (s) =>
          (s.case?.caseNumber?.toLowerCase().includes(q) ?? false) ||
          getSendOutDestination(s).toLowerCase().includes(q) ||
          formatSendOutUserName(s.requestedBy).toLowerCase().includes(q)
      );
    }

    if (sendOutFilters.requestTypes.length > 0) {
      result = result.filter((s) =>
        sendOutFilters.requestTypes.includes(s.requestType)
      );
    }

    if (sendOutFilters.priorities.length > 0) {
      result = result.filter(
        (s) =>
          s.priority != null && sendOutFilters.priorities.includes(s.priority)
      );
    }

    if (sendOutFilters.destinations.length > 0) {
      result = result.filter((s) =>
        sendOutFilters.destinations.includes(getSendOutDestination(s))
      );
    }

    if (sendOutFilters.requestedBys.length > 0) {
      result = result.filter((s) =>
        sendOutFilters.requestedBys.includes(
          formatSendOutUserName(s.requestedBy)
        )
      );
    }

    if (sendOutFilters.externalInstitutionReasons.length > 0) {
      result = result.filter(
        (s) =>
          s.externalInstitutionReason != null &&
          sendOutFilters.externalInstitutionReasons.includes(
            s.externalInstitutionReason
          )
      );
    }

    return result;
  }, [
    pendingSendOuts,
    debouncedSendOutSearch,
    sendOutFilters.requestTypes,
    sendOutFilters.priorities,
    sendOutFilters.destinations,
    sendOutFilters.requestedBys,
    sendOutFilters.externalInstitutionReasons,
  ]);

  // ==================== PAGINATION ====================

  const casesTotalPages = Math.ceil(filteredCases.length / ITEMS_PER_PAGE);
  const paginatedCases = useMemo(() => {
    const start = (caseFilters.page - 1) * ITEMS_PER_PAGE;
    return filteredCases.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCases, caseFilters.page]);

  const sendOutsTotalPages = Math.ceil(
    filteredSendOuts.length / ITEMS_PER_PAGE
  );
  const paginatedSendOuts = useMemo(() => {
    const start = (sendOutFilters.page - 1) * ITEMS_PER_PAGE;
    return filteredSendOuts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSendOuts, sendOutFilters.page]);

  // ==================== ACTIVE FILTER FLAGS ====================

  const hasActiveCaseFilters =
    caseFilters.caseType !== null ||
    caseFilters.pathologists.length > 0 ||
    caseFilters.sendingPractices.length > 0;

  const hasActiveSendOutFilters =
    sendOutFilters.requestTypes.length > 0 ||
    sendOutFilters.priorities.length > 0 ||
    sendOutFilters.destinations.length > 0 ||
    sendOutFilters.requestedBys.length > 0 ||
    sendOutFilters.externalInstitutionReasons.length > 0;

  // ==================== FILTER HANDLERS ====================

  const handleOpenFilterModal = () => {
    if (activeTab === "cases") {
      setPendingCaseFilters({ ...caseFilters });
    } else {
      setPendingSendOutFilters({ ...sendOutFilters });
    }
    openFilterModal();
  };

  const handleApplyFilters = () => {
    if (activeTab === "cases") {
      setCaseFilters({ ...pendingCaseFilters, page: 1 });
    } else {
      setSendOutFilters({ ...pendingSendOutFilters, page: 1 });
    }
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    if (activeTab === "cases") {
      setCaseFilters((prev) => ({
        ...defaultCaseFilters(),
        search: prev.search,
      }));
    } else {
      setSendOutFilters((prev) => ({
        ...defaultSendOutFilters(),
        search: prev.search,
      }));
    }
  };

  // ==================== CASE SELECTION ====================

  const casesWithValidAddress = useMemo(
    () =>
      new Set(filteredCases.filter((c) => !isCaseBlocked(c)).map((c) => c.id)),
    [filteredCases]
  );

  const validSelectedCases = useMemo(
    () =>
      Array.from(selectedCases).filter((id) => casesWithValidAddress.has(id)),
    [selectedCases, casesWithValidAddress]
  );

  const selectableOnPage = paginatedCases.filter((c) =>
    casesWithValidAddress.has(c.id)
  );
  const allSelectableSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((c) => selectedCases.has(c.id));
  const someSelected =
    selectableOnPage.some((c) => selectedCases.has(c.id)) &&
    !allSelectableSelected;

  const handleSelectAll = () => {
    if (paginatedCases.length === 0) return;
    const selectableCases = paginatedCases.filter((c) =>
      casesWithValidAddress.has(c.id)
    );
    if (selectableCases.every((c) => selectedCases.has(c.id))) {
      const newSet = new Set(selectedCases);
      paginatedCases.forEach((c) => newSet.delete(c.id));
      setSelectedCases(newSet);
    } else {
      const newSet = new Set(selectedCases);
      selectableCases.forEach((c) => newSet.add(c.id));
      setSelectedCases(newSet);
    }
  };

  const handleToggleSelect = (caseId: string) => {
    const newSet = new Set(selectedCases);
    if (newSet.has(caseId)) {
      newSet.delete(caseId);
    } else {
      newSet.add(caseId);
    }
    setSelectedCases(newSet);
  };

  // ==================== CONFIRM HANDLERS ====================

  const handleOpenBulkConfirm = useCallback(() => {
    bulkTrackingForm.reset();
    openConfirmModal();
  }, [bulkTrackingForm, openConfirmModal]);

  const handleCloseBulkConfirm = useCallback(() => {
    bulkTrackingForm.reset();
    closeConfirmModal();
  }, [bulkTrackingForm, closeConfirmModal]);

  const handleOpenSingleConfirm = useCallback((caseData: ShippingQueueCase) => {
    setSingleConfirmCase(caseData);
  }, []);

  const handleCloseSingleConfirm = useCallback(() => {
    setSingleConfirmCase(null);
  }, []);

  const handleConfirmSingle = useCallback(async () => {
    if (!singleConfirmCase) return;
    try {
      const result = await confirmMutation.mutateAsync([singleConfirmCase.id]);
      handleShippingNotification(result);
      setSelectedCases((prev) => {
        const newSet = new Set(prev);
        newSet.delete(singleConfirmCase.id);
        return newSet;
      });
      setSingleConfirmCase(null);
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm shipping",
        color: "red",
      });
    }
  }, [singleConfirmCase, confirmMutation]);

  const handleBulkConfirm = useCallback(async () => {
    if (validSelectedCases.length === 0) {
      notifications.show({
        title: "No Selection",
        message: "Please select at least one case with a valid address",
        color: "blue",
      });
      return;
    }
    try {
      const trackingNumber = bulkTrackingForm.values.trackingNumber;
      const result = await confirmMutation.mutateAsync(
        trackingNumber.trim()
          ? { caseIds: validSelectedCases, trackingNumber }
          : validSelectedCases
      );
      handleShippingNotification(result);
      setSelectedCases(new Set());
      handleCloseBulkConfirm();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm shipping",
        color: "red",
      });
    }
  }, [
    validSelectedCases,
    confirmMutation,
    bulkTrackingForm,
    handleCloseBulkConfirm,
  ]);

  const handleOpenSendOutConfirm = (sendOut: SendOut) => {
    setSendOutToConfirm(sendOut);
  };

  const handleCloseSendOutConfirm = () => {
    setSendOutToConfirm(null);
  };

  const handleConfirmSendOutShipping = async () => {
    if (!sendOutToConfirm) return;
    try {
      await markShippedMutation.mutateAsync({ id: sendOutToConfirm.id });
      notifications.show({
        title: "Success",
        message: `Send-out for ${
          sendOutToConfirm.case?.caseNumber || "case"
        } marked as shipped`,
        color: "green",
      });
      setSendOutToConfirm(null);
      refetchSendOuts();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm shipping",
        color: "red",
      });
    }
  };

  const handleQueueTabChange = useCallback(
    (value: string | null) => {
      if (value === "cases" || value === "send-outs") {
        setActiveTab(value);
        refetch();
        refetchSendOuts();
      }
    },
    [refetch, refetchSendOuts]
  );

  const handleCasesTabChange = useCallback((value: string | null) => {
    if (value === "pending" || value === "shipped") {
      setActiveCasesTab(value);
    }
  }, []);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* ===== HEADER ===== */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Shipping Queue</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Ship slides to external pathologists or return to originating
                practices for TC cases
              </Text>
            </div>
            {activeTab === "cases" &&
              activeCasesTab === "pending" &&
              validSelectedCases.length > 0 && (
                <Button
                  leftSection={<IconCheck size={16} />}
                  onClick={handleOpenBulkConfirm}
                  color="green"
                >
                  Confirm Shipping ({validSelectedCases.length})
                </Button>
              )}
          </Group>

          {/* ===== TABS ===== */}
          <Tabs
            value={activeTab}
            onChange={handleQueueTabChange}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab
                value="cases"
                rightSection={
                  filteredCases.length > 0 ? (
                    <Badge
                      size="xs"
                      color="blue"
                      variant="filled"
                      style={{ minWidth: 24 }}
                    >
                      {filteredCases.length}
                    </Badge>
                  ) : undefined
                }
              >
                Cases
              </Tabs.Tab>
              <Tabs.Tab
                value="send-outs"
                rightSection={
                  filteredSendOuts.length > 0 ? (
                    <Badge
                      size="xs"
                      color="orange"
                      variant="filled"
                      style={{ minWidth: 24 }}
                    >
                      {filteredSendOuts.length}
                    </Badge>
                  ) : undefined
                }
              >
                Send-Outs
              </Tabs.Tab>
            </Tabs.List>

            {/* ===== CASES TAB ===== */}
            <Tabs.Panel value="cases" pt="md">
              <Tabs
                value={activeCasesTab}
                onChange={handleCasesTabChange}
                keepMounted={false}
              >
                <Tabs.List>
                  <Tabs.Tab value="pending">Pending Shipping</Tabs.Tab>
                  <Tabs.Tab value="shipped">Shipped</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="pending" pt="md">
                  <Stack gap="md">
                    <Group align="stretch" grow>
                      <Paper p="md" withBorder style={{ flex: 2 }}>
                        <Stack gap="sm">
                          <Group>
                            <IconSearch size={20} />
                            <Text fw={500}>Search</Text>
                          </Group>
                          <TextInput
                            placeholder="Search by case number or patient name..."
                            value={caseFilters.search}
                            onChange={(e) =>
                              setCaseFilters((prev) => ({
                                ...prev,
                                search: e.target.value,
                                page: 1,
                              }))
                            }
                            leftSection={<IconSearch size={16} />}
                          />
                        </Stack>
                      </Paper>
                      <Paper p="md" withBorder style={{ flex: 1 }}>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Group>
                              <IconFilter size={20} />
                              <Text fw={500}>Filters</Text>
                            </Group>
                            <Button
                              variant="subtle"
                              size="compact-sm"
                              leftSection={<IconRefresh size={14} />}
                              onClick={() => refetch()}
                              loading={isFetching}
                            >
                              Refresh
                            </Button>
                          </Group>
                          <Button
                            variant="light"
                            onClick={handleOpenFilterModal}
                            fullWidth
                          >
                            {hasActiveCaseFilters
                              ? "Edit Filters"
                              : "Add Filters"}
                          </Button>
                          {hasActiveCaseFilters && (
                            <Group gap="xs" align="center" wrap="wrap">
                              <Text size="sm" c="dimmed">
                                Active:
                              </Text>
                              {caseFilters.caseType && (
                                <Pill
                                  withRemoveButton
                                  onRemove={() =>
                                    setCaseFilters((prev) => ({
                                      ...prev,
                                      caseType: null,
                                      page: 1,
                                    }))
                                  }
                                >
                                  {caseFilters.caseType === "tc_return"
                                    ? "TC Return"
                                    : "Remote"}
                                </Pill>
                              )}
                              {caseFilters.pathologists.map((p) => (
                                <Pill
                                  key={p}
                                  withRemoveButton
                                  onRemove={() =>
                                    setCaseFilters((prev) => ({
                                      ...prev,
                                      pathologists: prev.pathologists.filter(
                                        (x) => x !== p
                                      ),
                                      page: 1,
                                    }))
                                  }
                                >
                                  {p}
                                </Pill>
                              ))}
                              {caseFilters.sendingPractices.map((sp) => (
                                <Pill
                                  key={sp}
                                  withRemoveButton
                                  onRemove={() =>
                                    setCaseFilters((prev) => ({
                                      ...prev,
                                      sendingPractices:
                                        prev.sendingPractices.filter(
                                          (x) => x !== sp
                                        ),
                                      page: 1,
                                    }))
                                  }
                                >
                                  {sp}
                                </Pill>
                              ))}
                              <Button
                                variant="subtle"
                                size="compact-xs"
                                color="gray"
                                onClick={handleClearAllFilters}
                              >
                                Clear All
                              </Button>
                            </Group>
                          )}
                          <Text size="sm" c="dimmed">
                            Showing {filteredCases.length} of{" "}
                            {shippingQueueCases.length} items
                          </Text>
                        </Stack>
                      </Paper>
                    </Group>
                    <Paper p="md" withBorder>
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Group gap="xs">
                            <Text fw={500} size="lg">
                              Cases Pending Shipping ({filteredCases.length})
                            </Text>
                            {selectedCases.size > 0 && (
                              <Badge variant="light" color="green">
                                {validSelectedCases.length} selected
                              </Badge>
                            )}
                          </Group>
                          {casesTotalPages > 1 && (
                            <Group gap="md">
                              <Text size="sm" c="dimmed">
                                Showing{" "}
                                {(caseFilters.page - 1) * ITEMS_PER_PAGE + 1}
                                {"\u2013"}
                                {Math.min(
                                  caseFilters.page * ITEMS_PER_PAGE,
                                  filteredCases.length
                                )}{" "}
                                of {filteredCases.length}
                              </Text>
                              <Pagination
                                total={casesTotalPages}
                                value={caseFilters.page}
                                onChange={(p) =>
                                  setCaseFilters((prev) => ({
                                    ...prev,
                                    page: p,
                                  }))
                                }
                                size="sm"
                              />
                            </Group>
                          )}
                        </Group>

                        <Group>
                          <Checkbox
                            checked={allSelectableSelected}
                            indeterminate={someSelected}
                            onChange={handleSelectAll}
                            disabled={selectableOnPage.length === 0}
                            label="Select All"
                          />
                          {selectedCases.size > 0 && (
                            <Text size="sm" c="dimmed">
                              {validSelectedCases.length} of{" "}
                              {filteredCases.length} selected
                            </Text>
                          )}
                        </Group>

                        {isLoading ? (
                          <Center p="xl">
                            <Loader />
                          </Center>
                        ) : filteredCases.length === 0 ? (
                          <Alert
                            icon={<IconAlertCircle size={16} />}
                            title="No Cases"
                          >
                            {hasActiveCaseFilters || caseFilters.search
                              ? "No cases match the current filters"
                              : "No cases pending shipping"}
                          </Alert>
                        ) : (
                          <Table striped highlightOnHover>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th style={{ width: 50 }}>
                                  Select
                                </Table.Th>
                                <Table.Th
                                  style={{ width: 130, whiteSpace: "nowrap" }}
                                >
                                  Case Number
                                </Table.Th>
                                <Table.Th style={{ width: 150 }}>
                                  Patient
                                </Table.Th>
                                <Table.Th style={{ width: 140 }}>Type</Table.Th>
                                <Table.Th style={{ width: 140 }}>
                                  Status
                                </Table.Th>
                                <Table.Th
                                  style={{ width: 75, textAlign: "right" }}
                                >
                                  Blocks
                                </Table.Th>
                                <Table.Th
                                  style={{ width: 75, textAlign: "right" }}
                                >
                                  Slides
                                </Table.Th>
                                <Table.Th>Ship To</Table.Th>
                                <Table.Th style={{ width: 100 }}>
                                  Actions
                                </Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {paginatedCases.map((caseData) => {
                                const isSelected = selectedCases.has(
                                  caseData.id
                                );
                                const { display: shipToDisplay, hasAddress } =
                                  getShippingAddress(caseData);
                                const isBlocked = isCaseBlocked(caseData);

                                return (
                                  <Table.Tr key={caseData.id}>
                                    <Table.Td>
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() =>
                                          handleToggleSelect(caseData.id)
                                        }
                                        disabled={isBlocked}
                                      />
                                    </Table.Td>
                                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                                      <Text size="sm" fw={500}>
                                        {caseData.caseNumber}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td>
                                      {caseData.patient ? (
                                        <Text size="sm">
                                          {caseData.patient.firstName}{" "}
                                          {caseData.patient.lastName}
                                        </Text>
                                      ) : (
                                        <Text size="sm" c="dimmed" fs="italic">
                                          N/A
                                        </Text>
                                      )}
                                    </Table.Td>
                                    <Table.Td>
                                      <Badge
                                        color={
                                          getCaseType(
                                            caseData.accessioningType
                                          ) === "tc_return"
                                            ? "violet"
                                            : "blue"
                                        }
                                        variant="light"
                                      >
                                        {getTypeLabel(
                                          caseData.accessioningType
                                        )}
                                      </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                      {(() => {
                                        const config =
                                          getStatusBadgeConfig(caseData);
                                        if (!config.show) return null;
                                        return (
                                          <Tooltip
                                            label={
                                              caseData.shippingBlockMessage ||
                                              "Address information is missing or invalid"
                                            }
                                            withinPortal
                                            position="top"
                                            disabled={
                                              caseData.shippingEligibility !==
                                              "blocked"
                                            }
                                          >
                                            <Badge
                                              color={config.color}
                                              variant="light"
                                              style={{
                                                cursor:
                                                  caseData.shippingEligibility ===
                                                  "blocked"
                                                    ? "pointer"
                                                    : "default",
                                              }}
                                            >
                                              {config.label}
                                            </Badge>
                                          </Tooltip>
                                        );
                                      })()}
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: "right" }}>
                                      {caseData.blockCount ?? "—"}
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: "right" }}>
                                      {caseData.slideCount ?? "—"}
                                    </Table.Td>
                                    <Table.Td>
                                      <Stack gap={2}>
                                        {hasAddress ? (
                                          <Text size="sm" lineClamp={1}>
                                            {shipToDisplay}
                                          </Text>
                                        ) : (
                                          <Text
                                            size="sm"
                                            c="dimmed"
                                            fs="italic"
                                          >
                                            No address on file
                                          </Text>
                                        )}
                                        {isBlocked && (
                                          <Stack gap={4}>
                                            <Text size="xs" c="red.7" fw={500}>
                                              {caseData.shippingBlockMessage ||
                                                "Please add the remote shipping address in Admin → Users/Physicians, then retry."}
                                            </Text>
                                            {(() => {
                                              if (
                                                caseData.destinationSource ===
                                                  "remote_pathologist_user" &&
                                                caseData.pathologist?.id
                                              ) {
                                                if (canManageUsers) {
                                                  return (
                                                    <Anchor
                                                      href={`/admin/users?user=${caseData.pathologist.id}`}
                                                      onClick={handleAdminRemediationClick(
                                                        `/admin/users?user=${caseData.pathologist.id}`
                                                      )}
                                                      size="xs"
                                                      fw={600}
                                                    >
                                                      Fix in Admin → Users
                                                    </Anchor>
                                                  );
                                                }
                                                return (
                                                  <Text size="xs" c="dimmed">
                                                    Contact an administrator to
                                                    update the pathologist's
                                                    address.
                                                  </Text>
                                                );
                                              }
                                              if (
                                                caseData.destinationSource ===
                                                  "remote_physician" &&
                                                caseData.physician?.id
                                              ) {
                                                if (canManagePhysicians) {
                                                  return (
                                                    <Anchor
                                                      href={`/admin/physicians?physician=${caseData.physician.id}`}
                                                      onClick={handleAdminRemediationClick(
                                                        `/admin/physicians?physician=${caseData.physician.id}`
                                                      )}
                                                      size="xs"
                                                      fw={600}
                                                    >
                                                      Fix in Admin → Physicians
                                                    </Anchor>
                                                  );
                                                }
                                                return (
                                                  <Text size="xs" c="dimmed">
                                                    Contact an administrator to
                                                    update the physician's
                                                    address.
                                                  </Text>
                                                );
                                              }
                                              if (
                                                caseData.destinationSource ===
                                                "tc_case_fields"
                                              ) {
                                                if (canUpdateCases) {
                                                  return (
                                                    <Anchor
                                                      component={Link}
                                                      to={`/cases/${caseData.id}`}
                                                      size="xs"
                                                      fw={600}
                                                    >
                                                      Fix in Case → Set TC
                                                      shipping address
                                                    </Anchor>
                                                  );
                                                }
                                                return (
                                                  <Text size="xs" c="dimmed">
                                                    Contact front office to set
                                                    the TC shipping address.
                                                  </Text>
                                                );
                                              }
                                              return null;
                                            })()}
                                          </Stack>
                                        )}
                                      </Stack>
                                    </Table.Td>
                                    <Table.Td>
                                      <Button
                                        size="xs"
                                        color="green"
                                        onClick={() =>
                                          handleOpenSingleConfirm(caseData)
                                        }
                                        loading={confirmMutation.isPending}
                                        disabled={isBlocked}
                                      >
                                        Confirm
                                      </Button>
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        )}
                      </Stack>
                    </Paper>
                  </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="shipped" pt="md">
                  <ShippedCasesPanel />
                </Tabs.Panel>
              </Tabs>
            </Tabs.Panel>

            {/* ===== SEND-OUTS TAB ===== */}
            <Tabs.Panel value="send-outs" pt="md">
              <Stack gap="md">
                <Group align="stretch" grow>
                  <Paper p="md" withBorder style={{ flex: 2 }}>
                    <Stack gap="sm">
                      <Group>
                        <IconSearch size={20} />
                        <Text fw={500}>Search</Text>
                      </Group>
                      <TextInput
                        placeholder="Search by case #, destination, or requester..."
                        value={sendOutFilters.search}
                        onChange={(e) =>
                          setSendOutFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                            page: 1,
                          }))
                        }
                        leftSection={<IconSearch size={16} />}
                      />
                    </Stack>
                  </Paper>
                  <Paper p="md" withBorder style={{ flex: 1 }}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group>
                          <IconFilter size={20} />
                          <Text fw={500}>Filters</Text>
                        </Group>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => refetchSendOuts()}
                          loading={fetchingSendOuts}
                        >
                          Refresh
                        </Button>
                      </Group>
                      <Button
                        variant="light"
                        onClick={handleOpenFilterModal}
                        fullWidth
                      >
                        {hasActiveSendOutFilters
                          ? "Edit Filters"
                          : "Add Filters"}
                      </Button>
                      {hasActiveSendOutFilters && (
                        <Group gap="xs" align="center" wrap="wrap">
                          <Text size="sm" c="dimmed">
                            Active:
                          </Text>
                          {sendOutFilters.requestTypes.map((rt) => (
                            <Pill
                              key={rt}
                              withRemoveButton
                              onRemove={() =>
                                setSendOutFilters((prev) => ({
                                  ...prev,
                                  requestTypes: prev.requestTypes.filter(
                                    (x) => x !== rt
                                  ),
                                  page: 1,
                                }))
                              }
                            >
                              {REQUEST_TYPE_SHORT_LABELS[rt] || rt}
                            </Pill>
                          ))}
                          {sendOutFilters.priorities.map((p) => (
                            <Pill
                              key={p}
                              withRemoveButton
                              onRemove={() =>
                                setSendOutFilters((prev) => ({
                                  ...prev,
                                  priorities: prev.priorities.filter(
                                    (x) => x !== p
                                  ),
                                  page: 1,
                                }))
                              }
                            >
                              {PRIORITY_LABELS[p] || p}
                            </Pill>
                          ))}
                          {sendOutFilters.destinations.map((d) => (
                            <Pill
                              key={d}
                              withRemoveButton
                              onRemove={() =>
                                setSendOutFilters((prev) => ({
                                  ...prev,
                                  destinations: prev.destinations.filter(
                                    (x) => x !== d
                                  ),
                                  page: 1,
                                }))
                              }
                            >
                              {d}
                            </Pill>
                          ))}
                          {sendOutFilters.requestedBys.map((r) => (
                            <Pill
                              key={r}
                              withRemoveButton
                              onRemove={() =>
                                setSendOutFilters((prev) => ({
                                  ...prev,
                                  requestedBys: prev.requestedBys.filter(
                                    (x) => x !== r
                                  ),
                                  page: 1,
                                }))
                              }
                            >
                              {r}
                            </Pill>
                          ))}
                          {sendOutFilters.externalInstitutionReasons.map(
                            (r) => (
                              <Pill
                                key={r}
                                withRemoveButton
                                onRemove={() =>
                                  setSendOutFilters((prev) => ({
                                    ...prev,
                                    externalInstitutionReasons:
                                      prev.externalInstitutionReasons.filter(
                                        (x) => x !== r
                                      ),
                                    page: 1,
                                  }))
                                }
                              >
                                {EXTERNAL_REASON_SHORT_LABELS[r] || r}
                              </Pill>
                            )
                          )}
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            color="gray"
                            onClick={handleClearAllFilters}
                          >
                            Clear All
                          </Button>
                        </Group>
                      )}
                      <Text size="sm" c="dimmed">
                        Showing {filteredSendOuts.length} of{" "}
                        {pendingSendOuts.length} items
                      </Text>
                    </Stack>
                  </Paper>
                </Group>
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconPackage size={20} />
                        <Text fw={500} size="lg">
                          Send-Outs Pending Shipping ({filteredSendOuts.length})
                        </Text>
                      </Group>
                      {sendOutsTotalPages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing{" "}
                            {(sendOutFilters.page - 1) * ITEMS_PER_PAGE + 1}
                            {"\u2013"}
                            {Math.min(
                              sendOutFilters.page * ITEMS_PER_PAGE,
                              filteredSendOuts.length
                            )}{" "}
                            of {filteredSendOuts.length}
                          </Text>
                          <Pagination
                            total={sendOutsTotalPages}
                            value={sendOutFilters.page}
                            onChange={(p) =>
                              setSendOutFilters((prev) => ({
                                ...prev,
                                page: p,
                              }))
                            }
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {loadingSendOuts ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : filteredSendOuts.length === 0 ? (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        title="No Send-Outs"
                      >
                        {hasActiveSendOutFilters || sendOutFilters.search
                          ? "No send-outs match the current filters"
                          : "No send-outs pending shipping"}
                      </Alert>
                    ) : (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Case #</Table.Th>
                            <Table.Th>Type / Reason</Table.Th>
                            <Table.Th>Ordering {"\u2192"} Destination</Table.Th>
                            <Table.Th>Contact Person</Table.Th>
                            <Table.Th>Requested By</Table.Th>
                            <Table.Th>Materials</Table.Th>
                            <Table.Th>Approved By</Table.Th>
                            <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {paginatedSendOuts.map((sendOut) => (
                            <Table.Tr key={sendOut.id}>
                              <Table.Td
                                style={{ minWidth: 120, whiteSpace: "nowrap" }}
                              >
                                <Text fw={500}>
                                  {sendOut.case?.caseNumber || "N/A"}
                                </Text>
                                {getSendOutPriorityBadge(sendOut)}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {getSendOutTypeLabel(sendOut.requestType)}
                                </Text>
                                {formatSendOutExternalReason(sendOut) !==
                                  "—" && (
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    lineClamp={2}
                                    style={{ maxWidth: 220 }}
                                  >
                                    {stripSendOutHtml(
                                      formatSendOutExternalReason(sendOut)
                                    )}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {sendOut.orderingInstitution?.name ||
                                    sendOut.case?.facility?.name ||
                                    "—"}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {"\u2192"}
                                </Text>
                                <Text size="sm">
                                  {getSendOutDestination(sendOut)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {sendOut.contactPerson ? (
                                  <>
                                    <Text size="sm">
                                      {sendOut.contactPerson}
                                    </Text>
                                    {sendOut.contactPhoneFax && (
                                      <Text size="xs" c="dimmed">
                                        {sendOut.contactPhoneFax}
                                      </Text>
                                    )}
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {"\u2014"}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {formatSendOutUserName(sendOut.requestedBy)}
                                </Text>
                                <Tooltip
                                  label={dayjs(sendOut.createdAt).format(
                                    "MMM D, YYYY h:mm A"
                                  )}
                                >
                                  <Text size="xs" c="dimmed">
                                    {dayjs(sendOut.createdAt).fromNow()}
                                  </Text>
                                </Tooltip>
                              </Table.Td>
                              <Table.Td>
                                {getSendOutMaterialsSummary(sendOut)}
                              </Table.Td>
                              <Table.Td>
                                {sendOut.approvedBy ? (
                                  <>
                                    <Text size="sm">
                                      {formatSendOutUserName(
                                        sendOut.approvedBy
                                      )}
                                    </Text>
                                    {sendOut.approvedAt && (
                                      <Tooltip
                                        label={dayjs(sendOut.approvedAt).format(
                                          "MMM D, YYYY h:mm A"
                                        )}
                                      >
                                        <Text size="xs" c="dimmed">
                                          {dayjs(sendOut.approvedAt).fromNow()}
                                        </Text>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {"\u2014"}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Button
                                  size="xs"
                                  color="green"
                                  onClick={() =>
                                    handleOpenSendOutConfirm(sendOut)
                                  }
                                  loading={markShippedMutation.isPending}
                                >
                                  Confirm
                                </Button>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* ===== FILTER MODAL ===== */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title={activeTab === "cases" ? "Filter Cases" : "Filter Send-Outs"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            {activeTab === "cases" ? (
              <>
                <Select
                  label="Case Type"
                  placeholder="All types"
                  data={typeFilterOptions}
                  value={pendingCaseFilters.caseType}
                  onChange={(v) =>
                    setPendingCaseFilters((prev) => ({ ...prev, caseType: v }))
                  }
                  clearable
                />
                {pathologistOptions.length > 0 && (
                  <MultiSelect
                    label="Pathologist"
                    placeholder="All pathologists"
                    data={pathologistOptions}
                    value={pendingCaseFilters.pathologists}
                    onChange={(v) =>
                      setPendingCaseFilters((prev) => ({
                        ...prev,
                        pathologists: v,
                      }))
                    }
                    searchable
                    clearable
                  />
                )}
                {sendingPracticeOptions.length > 0 && (
                  <MultiSelect
                    label="Sending Practice"
                    placeholder="All practices"
                    data={sendingPracticeOptions}
                    value={pendingCaseFilters.sendingPractices}
                    onChange={(v) =>
                      setPendingCaseFilters((prev) => ({
                        ...prev,
                        sendingPractices: v,
                      }))
                    }
                    searchable
                    clearable
                  />
                )}
              </>
            ) : (
              <>
                <MultiSelect
                  label="Request Type"
                  placeholder="All types"
                  data={REQUEST_TYPE_OPTIONS}
                  value={pendingSendOutFilters.requestTypes}
                  onChange={(v) =>
                    setPendingSendOutFilters((prev) => ({
                      ...prev,
                      requestTypes: v,
                    }))
                  }
                  clearable
                />
                <MultiSelect
                  label="Priority"
                  placeholder="All priorities"
                  data={PRIORITY_OPTIONS}
                  value={pendingSendOutFilters.priorities}
                  onChange={(v) =>
                    setPendingSendOutFilters((prev) => ({
                      ...prev,
                      priorities: v,
                    }))
                  }
                  clearable
                />
                <MultiSelect
                  label="Destination"
                  placeholder="All destinations"
                  data={sendOutDestinationOptions}
                  value={pendingSendOutFilters.destinations}
                  onChange={(v) =>
                    setPendingSendOutFilters((prev) => ({
                      ...prev,
                      destinations: v,
                    }))
                  }
                  searchable
                  clearable
                />
                <MultiSelect
                  label="Requested By"
                  placeholder="All requesters"
                  data={sendOutRequestedByOptions}
                  value={pendingSendOutFilters.requestedBys}
                  onChange={(v) =>
                    setPendingSendOutFilters((prev) => ({
                      ...prev,
                      requestedBys: v,
                    }))
                  }
                  searchable
                  clearable
                />
                <MultiSelect
                  label="External Request Reason"
                  placeholder="All reasons"
                  data={EXTERNAL_REASON_OPTIONS}
                  value={pendingSendOutFilters.externalInstitutionReasons}
                  onChange={(v) =>
                    setPendingSendOutFilters((prev) => ({
                      ...prev,
                      externalInstitutionReasons: v,
                    }))
                  }
                  clearable
                />
              </>
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* ===== SINGLE CASE CONFIRM MODAL ===== */}
        <Modal
          opened={!!singleConfirmCase}
          onClose={handleCloseSingleConfirm}
          title="Confirm Shipping"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to confirm shipping for case{" "}
              <strong>{singleConfirmCase?.caseNumber}</strong>?
            </Text>

            {singleConfirmCase && (
              <Paper p="sm" withBorder bg="gray.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Patient:
                    </Text>
                    <Text size="sm">
                      {singleConfirmCase.patient
                        ? `${singleConfirmCase.patient.firstName} ${singleConfirmCase.patient.lastName}`
                        : "N/A"}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Ship To:
                    </Text>
                    <Text size="sm">
                      {getShippingAddress(singleConfirmCase).display}
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            )}

            <Text size="sm" c="dimmed">
              The case will be marked as &quot;Shipped&quot; and removed from
              this queue.
            </Text>

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={handleCloseSingleConfirm}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleConfirmSingle}
                loading={confirmMutation.isPending}
                leftSection={<IconCheck size={16} />}
              >
                Confirm Shipping
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* ===== BULK CONFIRM MODAL ===== */}
        <Modal
          opened={confirmModalOpened}
          onClose={handleCloseBulkConfirm}
          title="Confirm Shipping"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to confirm shipping for{" "}
              <strong>{validSelectedCases.length}</strong> case
              {validSelectedCases.length !== 1 ? "s" : ""}?
            </Text>

            {selectedCases.size !== validSelectedCases.length && (
              <Alert color="orange" icon={<IconAlertCircle size={16} />}>
                {selectedCases.size - validSelectedCases.length} case(s) will be
                skipped because they are blocked or don&apos;t have a valid
                shipping address.
              </Alert>
            )}

            <TextInput
              label="Tracking number"
              description="Optional — shared by the selected cases in this package"
              placeholder="Enter a tracking number"
              {...bulkTrackingForm.getInputProps("trackingNumber")}
            />

            {(() => {
              const selectedCasesData = filteredCases.filter((c) =>
                validSelectedCases.includes(c.id)
              );
              const remoteCount = selectedCasesData.filter(
                (c) => getCaseType(c.accessioningType) === "remote"
              ).length;
              const tcReturnCount = selectedCasesData.filter(
                (c) => getCaseType(c.accessioningType) === "tc_return"
              ).length;

              return (
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Cases will be updated as follows:
                  </Text>
                  <Stack gap={4} pl="md">
                    {remoteCount > 0 && (
                      <Text size="sm" c="dimmed">
                        {"\u2022"} {remoteCount} case
                        {remoteCount !== 1 ? "s" : ""} {"\u2192"}{" "}
                        <Text span fw={500} c="blue">
                          Shipped
                        </Text>
                      </Text>
                    )}
                    {tcReturnCount > 0 && (
                      <Text size="sm" c="dimmed">
                        {"\u2022"} {tcReturnCount} TC case
                        {tcReturnCount !== 1 ? "s" : ""} {"\u2192"}{" "}
                        <Text span fw={500} c="green">
                          Shipped
                        </Text>
                      </Text>
                    )}
                  </Stack>
                </Stack>
              );
            })()}

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={handleCloseBulkConfirm}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleBulkConfirm}
                loading={confirmMutation.isPending}
                leftSection={<IconCheck size={16} />}
              >
                Confirm Shipping
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* ===== SEND-OUT CONFIRM MODAL ===== */}
        <Modal
          opened={!!sendOutToConfirm}
          onClose={handleCloseSendOutConfirm}
          title="Confirm Send-Out Shipping"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to confirm shipping for this send-out?
            </Text>

            {sendOutToConfirm && (
              <Paper p="sm" withBorder bg="gray.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Case:
                    </Text>
                    <Text size="sm">
                      {sendOutToConfirm.case?.caseNumber || "N/A"}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Patient:
                    </Text>
                    <Text size="sm">
                      {sendOutToConfirm.case?.patient
                        ? `${sendOutToConfirm.case.patient.firstName} ${sendOutToConfirm.case.patient.lastName}`
                        : "N/A"}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Type:
                    </Text>
                    <Badge
                      size="sm"
                      color={
                        sendOutToConfirm.requestType ===
                        SendoutRequestType.EXTERNAL_INSTITUTION
                          ? "orange"
                          : "teal"
                      }
                      variant="light"
                    >
                      {getSendOutTypeLabel(sendOutToConfirm.requestType)}
                    </Badge>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Destination:
                    </Text>
                    <Text size="sm">
                      {getSendOutDestination(sendOutToConfirm)}
                    </Text>
                  </Group>
                  {formatSendOutExternalReason(sendOutToConfirm) !== "—" && (
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        Reason:
                      </Text>
                      <Text size="sm">
                        {stripSendOutHtml(
                          formatSendOutExternalReason(sendOutToConfirm)
                        )}
                      </Text>
                    </Group>
                  )}
                  {((sendOutToConfirm.slidesSent?.length ?? 0) > 0 ||
                    (sendOutToConfirm.blocksSent?.length ?? 0) > 0) && (
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        Materials:
                      </Text>
                      <Text size="sm">
                        {[
                          sendOutToConfirm.slidesSent?.length
                            ? `${sendOutToConfirm.slidesSent.length} slide(s)`
                            : null,
                          sendOutToConfirm.blocksSent?.length
                            ? `${sendOutToConfirm.blocksSent.length} block(s)`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Paper>
            )}

            <Text size="sm" c="dimmed">
              The send-out will be marked as &quot;Shipped&quot; and moved to
              the In Transit section of the Send-Outs Queue.
            </Text>

            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={handleCloseSendOutConfirm}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleConfirmSendOutShipping}
                loading={markShippedMutation.isPending}
                leftSection={<IconCheck size={16} />}
              >
                Confirm Shipping
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
